import toast from 'react-hot-toast'
import SignInLayout from 'src/layouts/signin-layout'
import FormItem from 'src/components/atoms/form-item'

import { useRouter } from 'src/hooks'
import { paths } from 'src/routes/paths'
import { useForm } from 'react-hook-form'
import { useWatch } from 'react-hook-form'
import { useEffect, useRef, useState } from 'react'
import { OTPProps } from 'antd/es/input/OTP'
import { useAuthContext } from 'src/auth/hooks'
import { yupResolver } from '@hookform/resolvers/yup'
import { Trans, useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'
import { changePassword, getSession } from 'src/services'
import { SignInDefaultValues, SignInSchema } from './schema'
import { LockOutlined, MailOutlined, WindowsOutlined } from '@ant-design/icons'

import {
  Col,
  Flex,
  Form,
  Input,
  Alert,
  Button,
  Divider,
  Typography,
} from 'antd'

type FormValueProps = {
  email: string
  password: string
}

function SignIn() {
  const router = useRouter()
  const numberCheckDigits = 6
  const submitFnRef = useRef<() => void>()

  const { i18n } = useTranslation()
  const { login, loginWithMicrosoft } = useAuthContext()

  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [searchParams] = useSearchParams()
  const [session, setSession] = useState('')
  const [seconds, setSeconds] = useState(120)
  const [loading, setLoading] = useState(false)
  const [disabled, setDisabled] = useState(true)
  const [authStep, setAuthStep] = useState<
    'initial' | 'credentials' | 'otp' | 'changePassword'
  >('initial')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isInternalUser, setIsInternalUser] = useState(false)
  const [isMicrosoftLoading, setIsMicrosoftLoading] = useState(false)
  const [passwordRequirements, setPasswordRequirements] = useState({
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
    minLength: false,
  })

  const {
    control,
    getValues,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValueProps>({
    resolver: yupResolver(SignInSchema),
    defaultValues: SignInDefaultValues,
  })

  const onSubmit = async ({ email, password }: FormValueProps) => {
    try {
      const res = await getSession({ email, password })
      if (res?.data.session) {
        setSession(res?.data.session)
        setError('')

        if (res?.data.hasToChangePassword) {
          setAuthStep('changePassword')
        } else {
          setAuthStep('otp')
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const errorCode = error.response?.data?.code
      const errorMessage = error.response?.data?.message

      if (errorCode === '99999999') {
        setError(i18n.t('signIn.invalid-username-password'))
      } else {
        setError(i18n.t([`signIn.${errorMessage}`, 'error.unspecific']))
      }
    }
  }

  const handleFinish = async () => {
    if (code.length < numberCheckDigits) return

    try {
      setError('')
      setLoading(true)
      const { email } = getValues()
      await login(code, session, email)
      const params = Object.fromEntries(searchParams.entries())

      if (params?.returnTo) {
        router.push(params.returnTo)
      } else {
        router.replace(paths.dashboard.root)
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const errorMessage = error.response?.data?.message
      setError(i18n.t([`signIn.${errorMessage}`, 'error.unspecific']))
    } finally {
      setLoading(false)
    }
  }

  const handleResendCode = () => {
    const { email, password } = getValues()
    setCode('')
    setError('')
    setSeconds(120)
    setDisabled(true)
    onSubmit({ email, password })
  }

  const onChange: NonNullable<OTPProps['onChange']> = (text) => {
    setCode(text)
  }

  useEffect(() => {
    if (seconds > 0) {
      const timer = setInterval(() => {
        setSeconds((prevSeconds) => prevSeconds - 1)
      }, 1000)

      return () => clearInterval(timer)
    } else {
      setDisabled(false)
    }
  }, [seconds])

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  const handleMicrosoftLogin = async () => {
    try {
      setIsMicrosoftLoading(true)
      await loginWithMicrosoft()
    } catch (error) {
      toast.error(i18n.t('error.signin.microsoft'))
    } finally {
      setIsMicrosoftLoading(false)
    }
  }

  const renderInitialStep = () => (
    <>
      <Button
        size="large"
        type="primary"
        loading={isMicrosoftLoading}
        onClick={handleMicrosoftLogin}
        data-testid="internal-user-btn"
        icon={isMicrosoftLoading ? null : <WindowsOutlined />}
      >
        {i18n.t('signIn.internalUser')}
      </Button>

      <Divider>{i18n.t('signIn.or')}</Divider>

      <Button
        size="large"
        data-testid="external-user-btn"
        onClick={() => setAuthStep('credentials')}
      >
        {i18n.t('signIn.externalUser')}
      </Button>
    </>
  )

  const email = useWatch({
    control,
    name: 'email',
  })

  useEffect(() => {
    if (email && email.endsWith('@pli-petronas.com')) {
      setIsInternalUser(true)
      setError(i18n.t('signIn.internalUserAlert'))
    } else {
      setError('')
      setIsInternalUser(false)
    }
  }, [email, i18n])

  const renderCredentialsStep = () => (
    <>
      <Typography.Text
        underline
        className="mb-4 block flex-1 cursor-pointer"
        onClick={() => setAuthStep('initial')}
      >
        ← {i18n.t('signIn.back')}
      </Typography.Text>

      <FormItem
        name="email"
        errors={errors}
        control={control}
        label={i18n.t('signIn.email')}
        component={
          <Input
            size="large"
            prefix={<MailOutlined />}
            data-testid="signin-input-email"
            aria-invalid={Boolean(errors.email)}
            placeholder={i18n.t('signIn.emailPlaceholder')}
          />
        }
      />

      {isInternalUser ? null : (
        <>
          <FormItem
            name="password"
            errors={errors}
            control={control}
            label={i18n.t('signIn.password')}
            component={
              <Input.Password
                size="large"
                prefix={<LockOutlined />}
                data-testid="signin-input-password"
                aria-invalid={Boolean(errors.password)}
                placeholder={i18n.t('signIn.passwordPlaceholder')}
              />
            }
          />

          <Flex wrap justify="end" className="mb-4 w-full">
            <Link
              to="/password-recovery"
              data-testid="forgot-password-btn"
              className="font-bold text-link"
            >
              {i18n.t('signIn.forgotPassword')}
            </Link>
          </Flex>
        </>
      )}

      <Button
        size="large"
        type="primary"
        loading={isSubmitting}
        disabled={isInternalUser}
        data-testid="signin-submit-btn"
        onClick={() => handleSubmit(onSubmit)()}
      >
        {i18n.t('signIn.submit')}
      </Button>
    </>
  )

  const renderOtpStep = () => (
    <>
      <Typography className="text-center">
        {i18n.t('signIn.otpInstruction')}
      </Typography>

      <Col xs={{ span: 20 }} md={{ span: 14 }} className="mx-auto py-8">
        <Input.OTP autoFocus size="large" value={code} onChange={onChange} />
      </Col>

      <Button
        size="large"
        className="my-3"
        loading={isSubmitting}
        onClick={handleResendCode}
        disabled={disabled || isSubmitting}
      >
        {i18n.t('signIn.resendCode')} {disabled ? formatTime(seconds) : null}
      </Button>

      <Button
        size="large"
        type="primary"
        loading={loading}
        onClick={handleFinish}
        disabled={code.length < numberCheckDigits}
      >
        {i18n.t('signIn.submit')}
      </Button>
    </>
  )

  const validatePassword = (password: string) => {
    setPasswordRequirements({
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      minLength: password.length >= 8,
    })
  }

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      setError(i18n.t('signIn.passwordMismatch'))
      return
    }

    if (
      !passwordRequirements.uppercase ||
      !passwordRequirements.number ||
      !passwordRequirements.special ||
      !passwordRequirements.minLength
    ) {
      setError(i18n.t('signIn.passwordRequirementsNotMet'))
      return
    }

    try {
      setLoading(true)

      const { email } = getValues()
      const { status } = await changePassword({ email, password: newPassword })

      if (status === 200) {
        setError('')
        setAuthStep('credentials')
        toast.success(i18n.t('signIn.passwordChanged'))
        await onSubmit({ email, password: newPassword })
      } else {
        setError(i18n.t('signIn.passwordChangeFailed'))
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const errorMessage = error.response?.data?.message
      setError(i18n.t([`signIn.${errorMessage}`, 'error.unspecific']))
    } finally {
      setLoading(false)
    }
  }

  const renderChangePasswordStep = () => (
    <>
      <Typography.Text
        underline
        className="mb-4 block cursor-pointer"
        onClick={() => setAuthStep('credentials')}
      >
        ← {i18n.t('signIn.back')}
      </Typography.Text>

      <div className="mb-4">
        <Typography.Text>{i18n.t('signIn.newPassword')}</Typography.Text>
        <Input.Password
          size="large"
          value={newPassword}
          className="mt-2 w-full"
          prefix={<LockOutlined />}
          placeholder={i18n.t('signIn.newPasswordPlaceholder')}
          onChange={(e) => {
            const value = e.target.value
            setNewPassword(value)
            validatePassword(value)
          }}
        />
      </div>

      <div className="mb-4 rounded-md bg-gray-100 p-4">
        <Typography.Text className="font-medium">
          {i18n.t('signIn.passwordRequirements')}
        </Typography.Text>
        <ul className="mt-2 pl-4">
          <li
            className={
              passwordRequirements.lowercase ? 'text-green-500' : 'text-red-500'
            }
          >
            {passwordRequirements.lowercase ? '✓' : 'x'}{' '}
            {i18n.t('signIn.passwordLowercase')}
          </li>
          <li
            className={
              passwordRequirements.uppercase ? 'text-green-500' : 'text-red-500'
            }
          >
            {passwordRequirements.uppercase ? '✓' : 'x'}{' '}
            {i18n.t('signIn.passwordUppercase')}
          </li>
          <li
            className={
              passwordRequirements.number ? 'text-green-500' : 'text-red-500'
            }
          >
            {passwordRequirements.number ? '✓' : 'x'}{' '}
            {i18n.t('signIn.passwordNumber')}
          </li>
          <li
            className={
              passwordRequirements.special ? 'text-green-500' : 'text-red-500'
            }
          >
            {passwordRequirements.special ? '✓' : 'x'}{' '}
            {i18n.t('signIn.passwordSpecial')}
          </li>
          <li
            className={
              passwordRequirements.minLength ? 'text-green-500' : 'text-red-500'
            }
          >
            {passwordRequirements.minLength ? '✓' : 'x'}{' '}
            {i18n.t('signIn.passwordMinLength')}
          </li>
        </ul>
      </div>

      <div className="mb-4">
        <Typography.Text>{i18n.t('signIn.confirmPassword')}</Typography.Text>
        <Input.Password
          size="large"
          className="mt-2 w-full"
          value={confirmPassword}
          prefix={<LockOutlined />}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder={i18n.t('signIn.confirmPasswordPlaceholder')}
        />
      </div>

      <Button
        size="large"
        type="primary"
        loading={loading}
        className="w-full"
        onClick={handleChangePassword}
        disabled={!newPassword || !confirmPassword || loading}
      >
        {i18n.t('signIn.changePassword')}
      </Button>
    </>
  )

  useEffect(() => {
    setError('')
    setNewPassword('')
    setConfirmPassword('')
    setPasswordRequirements({
      uppercase: false,
      lowercase: false,
      number: false,
      special: false,
      minLength: false,
    })
  }, [authStep])

  

  useEffect(() => {
    const handleEnter = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        submitFnRef.current?.()
      }
    }

    document.addEventListener('keydown', handleEnter)

    return () => {
      document.removeEventListener('keydown', handleEnter)
    }
  }, [])

  const renderAuthStep = () => {
    switch (authStep) {
      case 'initial':
        submitFnRef.current = undefined
        return renderInitialStep()
      case 'credentials':
        submitFnRef.current = handleSubmit(onSubmit)
        return renderCredentialsStep()
      case 'otp':
        submitFnRef.current = handleFinish
        return renderOtpStep()
      case 'changePassword':
        submitFnRef.current = handleChangePassword
        return renderChangePasswordStep()
      default:
        submitFnRef.current = undefined
        return renderInitialStep()
    }
  }

  return (
    <SignInLayout>
      <Form
        layout="vertical"
        wrapperCol={{ span: 24 }}
        onFinish={handleSubmit(onSubmit)}
        className="w-full max-w-[610px] p-4 md:w-[50vw]"
      >
        <Flex vertical align="start" className="w-full">
          <Flex vertical className="w-full">
            <Typography.Title level={1} className="text-center">
              <Trans
                i18n={i18n}
                i18nKey="signIn.title"
                components={[<strong />]}
              />
            </Typography.Title>

            <Divider />

            {error && (
              <Alert showIcon type="error" message={error} className="mb-4" />
            )}

            {renderAuthStep()}
          </Flex>
        </Flex>
      </Form>
    </SignInLayout>
  )
}

export default SignIn
